<template>
  <div>
    <v-navigation-drawer
      v-model="showFilters"
      clipped
      :temporary="$vuetify.breakpoint.xsOnly"
      :permanent="$vuetify.breakpoint.smAndUp"
      overflow
      hide-overlay
      app
      :style="{ marginTop: $vuetify.breakpoint.smAndDown ? '64px' : '128px' }"
    >
      <div class="pa-4">
        <div class="display-2 secondary--text mb-3">
          Filters
          <v-btn
            flat
            outline
            round
            color="primary"
            class="no-text-transform"
            @click="selectedFilters = []"
          >
            Clear all
          </v-btn>
        </div>
        <div
          v-for="service in sortedServices(currentTags)"
          :key="service.id"
          class="mb-3"
        >
          <div class="headline secondary--text pb-1">
            {{ service.title }}
          </div>
          <v-checkbox
            v-for="tag in sortedTags(service.tags)"
            :key="tag.id"
            v-model="selectedFilters"
            hide-details
            class="pb-1 ma-0"
            color="primary"
            :label="tag.title"
            :value="tag.title"
          >
            <div slot="label" class="primary--text">
              {{ tag.title }}
            </div>
          </v-checkbox>
        </div>
        <div class="mb-3">
          <div class="headline secondary--text pb-1">Location</div>
          <v-checkbox
            v-model="selectedFilters"
            hide-details
            class="pb-1 ma-0"
            value="_onbase"
            color="primary"
          >
            <div slot="label" class="primary--text">On Fort Meade</div>
          </v-checkbox>
          <v-checkbox
            v-model="selectedFilters"
            hide-details
            class="pb-1 ma-0"
            value="_offbase"
            color="primary"
          >
            <div slot="label" class="primary--text">Off Fort Meade</div>
          </v-checkbox>
        </div>
        <div style="height: 100px">
          <!--spacer to ensure back button doesn't cover up checkboxes-->
        </div>
      </div>
    </v-navigation-drawer>
    <v-content>
      <v-container>
        <v-layout row wrap align-end justify-space-between class="px-4 pt-2">
          <h1
            :class="[
              this.$vuetify.breakpoint.smAndDown ? 'display-1' : 'display-3',
              'secondary--text',
              'font-weight-bold'
            ]"
          >
            {{ currentCategory.title }}
          </h1>
          <div class="sort-container text-xs-right">
            <v-btn-toggle v-model="sortBy" mandatory>
              <v-btn flat value="alpha">
                <v-icon>sort_by_alpha</v-icon>
              </v-btn>
              <v-btn flat value="date">
                <v-icon>schedule</v-icon>
              </v-btn>
              <v-btn v-if="isUpcomingEventsCategory" flat value="event">
                <v-icon>event</v-icon>
              </v-btn>
            </v-btn-toggle>
            <div class="body-2 secondary--text font-weight-medium mt-1">
              {{ sortByLabel }}
            </div>
          </div>
        </v-layout>
        <v-container grid-list-xl fluid>
          <v-layout column>
            <div class="hidden-sm-and-up mb-2" :style="{ alignSelf: 'center' }">
              <v-btn color="primary" @click="toggleFilterSideNav">
                Show Filters
              </v-btn>
            </div>
            <v-flex v-for="listing in filteredListings" :key="listing.id">
              <fma-tile
                :title="listing.title"
                :description="listing.description"
                :action-on-tile-click="() => openDialog(listing)"
                line-clamp
              />
            </v-flex>
          </v-layout>
        </v-container>
      </v-container>
      <v-dialog
        v-model="dialogOpen"
        width="75vw"
        content-class="overflow-visible"
      >
        <v-card max-height="75vh">
          <v-card max-height="75vh" style="overflow: auto">
            <fma-listing :listing="currentDialogData" />
          </v-card>
          <v-btn
            id="close-button"
            icon
            color="primary"
            class="ma-0 fma-round-button"
            @click="dialogOpen = false"
          >
            <v-icon x-large> close </v-icon>
          </v-btn>
        </v-card>
      </v-dialog>
    </v-content>
    <div
      v-if="kioskMode"
      class="hidden-sm-and-down"
      style="position: fixed; bottom: 50px; left: 0; z-index: 10"
    >
      <fma-back-button />
    </div>
    <div class="scroll-to-top-container">
      <v-fade-transition>
        <v-btn
          v-show="showScrollButton"
          fab
          dark
          color="primary"
          @click="scrollToTop"
        >
          <v-icon>arrow_upward</v-icon>
        </v-btn>
      </v-fade-transition>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import axios from "axios";
import FmaTile from "@/components/FmaTile.vue";
import FmaListing from "@/components/FmaListing.vue";
import FmaBackButton from "@/components/FmaBackButton.vue";
import { mapState, mapGetters } from "vuex";

export default {
  components: {
    FmaTile,
    FmaListing,
    FmaBackButton
  },
  data() {
    return {
      currentDialogData: {},
      selectedFilters: [],
      dialogOpen: false,
      showFilters: false,
      showScrollButton: false,
      calendarEvents: [],
      calendarLoading: false,
      sortBy: "alpha"
    };
  },
  computed: {
    ...mapState({
      kioskMode: state => state.kioskMode,
      listingsByCategory: state => state.listingsByCategory
    }),
    ...mapGetters(["baseUrl"]),
    isUpcomingEventsCategory() {
      // Check if this is the "Upcoming Events" category (case-insensitive)
      const title = this.currentCategory.title || "";
      return (
        title.toLowerCase().includes("upcoming") &&
        title.toLowerCase().includes("event")
      );
    },
    sortByLabel() {
      switch (this.sortBy) {
        case "alpha":
          return "Sorted by name";
        case "date":
          return "Sorted by date posted";
        case "event":
          return "Sorted by soonest event";
        default:
          return "";
      }
    },
    filteredListings() {
      const locationFilters = this.selectedFilters.filter(
        f => f === "_onbase" || f === "_offbase"
      );
      const tagFilters = this.selectedFilters.filter(
        f => f !== "_onbase" && f !== "_offbase"
      );
      return this.sortedListings.filter(listing => {
        const passesLocation = locationFilters.every(f =>
          listing.tags.includes(f)
        );
        const passesTags =
          tagFilters.length === 0 ||
          tagFilters.some(f => listing.tags.includes(f));
        return passesLocation && passesTags;
      });
    },
    currentCategory() {
      return this.$store.state.currentCategory;
    },
    currentTags() {
      return this.$store.state.tagsByCategory;
    },
    sortedListings() {
      // Merge regular listings with calendar events (if Upcoming Events category)
      const regularListings = this.listingsByCategory || [];
      const allListings = this.isUpcomingEventsCategory
        ? [...regularListings, ...this.calendarEventsAsListings]
        : regularListings;

      if (this.sortBy === "date") {
        // Sort by published_on date (newest first)
        return _.orderBy(
          allListings,
          listing => {
            // Calendar events use their event start date
            if (listing.isCalendarEvent && listing.eventStart) {
              return new Date(listing.eventStart).getTime();
            }
            // Regular listings use published_on
            if (listing.published_on) {
              return new Date(listing.published_on).getTime();
            }
            return 0; // No date, sort to end
          },
          ["desc"]
        );
      } else if (this.sortBy === "event") {
        // Sort by event_on date (soonest first)
        // Listings without event_on go to the end, sorted by published_on
        const withEventDate = allListings.filter(
          l => l.event_on || l.eventStart
        );
        const withoutEventDate = allListings.filter(
          l => !l.event_on && !l.eventStart
        );

        // Sort listings with event dates by soonest first
        const sortedWithEvent = _.orderBy(
          withEventDate,
          listing => {
            const eventDate = listing.event_on || listing.eventStart;
            return new Date(eventDate).getTime();
          },
          ["asc"]
        );

        // Sort listings without event dates by published_on (newest first)
        const sortedWithoutEvent = _.orderBy(
          withoutEventDate,
          listing => {
            if (listing.published_on) {
              return new Date(listing.published_on).getTime();
            }
            return 0;
          },
          ["desc"]
        );

        return [...sortedWithEvent, ...sortedWithoutEvent];
      } else {
        // Default: sort alphabetically by title
        return _.sortBy(allListings, listing => _.toUpper(listing.title));
      }
    },
    calendarEventsAsListings() {
      // Convert calendar events to listing format matching FmaListing structure
      // Deduplicate recurring events - only show one instance per recurring series

      // Group events by recurringEventId (or their own id if not recurring)
      const eventGroups = {};
      for (const event of this.calendarEvents) {
        const groupKey = event.recurringEventId || event.id;
        if (!eventGroups[groupKey]) {
          eventGroups[groupKey] = [];
        }
        eventGroups[groupKey].push(event);
      }

      // For each group, take the first (soonest) instance
      const deduplicatedEvents = Object.values(eventGroups).map(group => {
        // Sort by start date and take the first one
        group.sort((a, b) => new Date(a.start) - new Date(b.start));
        return group[0];
      });

      return deduplicatedEvents.map(event => {
        const parsed = this.parseEventDescription(event.description || "");
        return {
          id: `calendar-${event.id}`,
          title: event.title,
          description: parsed.description || "",
          shields: parsed.pillars || [],
          address: parsed.address || event.location || "",
          phone: parsed.phone || "",
          dsnphone: "",
          email: parsed.email || "",
          website: parsed.website || "",
          onbase: true,
          tags: ["Upcoming Community Events", "_onbase"],
          isCalendarEvent: true,
          // For recurring events, use recurrencePattern; for one-time events, use event_on
          event_on: event.recurrencePattern ? null : event.start,
          recurrencePattern: event.recurrencePattern || null,
          eventStart: event.start,
          eventEnd: event.end,
          isAllDay: event.isAllDay
        };
      });
    },
    tileWidth() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return 12;
        case "sm":
          return 6;
        case "md":
          return 4;
        case "lg":
          return 3;
        case "xl":
          return 3;
        default:
          return 3; //TODO: is this right
      }
    }
  },
  mounted() {
    this.$store.dispatch("fetchCategory", {
      id: this.$route.params.categoryId
    });
    this.$store.dispatch("fetchListingsByCategory", {
      id: this.$route.params.categoryId
    });
    this.$store.dispatch("fetchTagsByCategory", {
      id: this.$route.params.categoryId
    });
    window.addEventListener("scroll", this.onScroll);

    // Fetch calendar events after category is loaded (for Upcoming Events)
    this.checkAndFetchCalendarEvents();
  },
  watch: {
    // Watch for category changes to fetch calendar events
    currentCategory: {
      handler() {
        this.checkAndFetchCalendarEvents();
      },
      deep: true
    },
    listingsByCategory: {
      handler(listings) {
        const targetId = this.$route.query.openListing;
        if (!targetId || !listings.length) return;
        const listing = listings.find(l => String(l.id) === String(targetId));
        if (listing) this.openDialog(listing);
      },
      immediate: true
    }
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll); // <-- 3. ADD THIS
  },
  methods: {
    openDialog(listing) {
      //Add google track for looking at a listing
      this.$gtag.screenview({
        app_name: "Fort Meade Resiliency Services",
        screen_name: listing.title
      });

      // update the dialog content to correspond to the tile that was opened
      this.currentDialogData = listing;
      // display the dialog
      this.dialogOpen = true;
    },
    sortedServices(services) {
      return _.sortBy(services, service => _.toUpper(service.title));
    },
    sortedTags(tags) {
      return _.sortBy(tags, tag => _.toUpper(tag.title));
    },
    toggleFilterSideNav() {
      this.showFilters = !this.showFilters;
    },
    onScroll() {
      // Show button when scrolled 200px or more
      const top = window.pageYOffset || document.documentElement.scrollTop;
      this.showScrollButton = top > 200;
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    async checkAndFetchCalendarEvents() {
      // Only fetch calendar events for "Upcoming Events" category
      if (
        this.isUpcomingEventsCategory &&
        !this.calendarLoading &&
        this.calendarEvents.length === 0
      ) {
        await this.fetchCalendarEvents();
      }
    },
    async fetchCalendarEvents() {
      this.calendarLoading = true;
      try {
        // Fetch events from today onwards (next 90 days)
        const now = new Date();
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + 90);

        const response = await axios.get(`${this.baseUrl}/calendar`, {
          params: {
            timeMin: now.toISOString(),
            timeMax: futureDate.toISOString()
          }
        });

        this.calendarEvents = response.data || [];
      } catch (err) {
        console.error("Failed to fetch calendar events for listings:", err);
        this.calendarEvents = [];
      } finally {
        this.calendarLoading = false;
      }
    },
    /**
     * Parses the Google Calendar event description to extract structured data.
     * Same logic as Calendar.vue for consistency.
     */
    parseEventDescription(rawDescription) {
      const result = {
        description: "",
        website: "",
        email: "",
        phone: "",
        address: "",
        pillars: []
      };

      if (!rawDescription) return result;

      // First, convert HTML to plain text
      let plainText = this.htmlToPlainText(rawDescription);

      // Find delimiter
      const delimiterMatch = plainText.match(
        /\n\s*-{3,}\s*\n|\n\s*-{3,}\s*$|^\s*-{3,}\s*\n|-{3,}/
      );

      if (!delimiterMatch) {
        result.description = plainText.trim();
        return result;
      }

      const delimiterIndex = delimiterMatch.index;
      result.description = plainText.substring(0, delimiterIndex).trim();
      const structuredPart = plainText.substring(
        delimiterIndex + delimiterMatch[0].length
      );

      const lines = structuredPart.split("\n");
      for (const line of lines) {
        let colonIndex = -1;
        for (let i = 0; i < line.length; i++) {
          if (line[i] === ":") {
            if (line.substring(i, i + 3) === "://") continue;
            colonIndex = i;
            break;
          }
        }

        if (colonIndex === -1) continue;

        const key = line
          .substring(0, colonIndex)
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "");
        const value = line.substring(colonIndex + 1).trim();

        if (!value) continue;

        if (
          this.matchesFieldName(key, ["website", "web", "site", "url", "link"])
        ) {
          result.website = value;
        } else if (this.matchesFieldName(key, ["email", "e-mail", "mail"])) {
          result.email = value;
        } else if (this.matchesFieldName(key, ["phone", "tel", "telephone"])) {
          result.phone = value;
        } else if (
          this.matchesFieldName(key, ["address", "addr", "location"])
        ) {
          result.address = value;
        } else if (
          this.matchesFieldName(key, ["pillars", "pillar", "shields", "shield"])
        ) {
          const rawPillars = value
            .split(",")
            .map(p => p.trim())
            .filter(p => p);
          result.pillars = rawPillars
            .map(p => this.normalizePillarName(p))
            .filter(p => p);
        }
      }

      return result;
    },
    htmlToPlainText(html) {
      if (!html) return "";
      let text = html;
      text = text.replace(/<br\s*\/?>/gi, "\n");
      text = text.replace(/<\/(p|div)>/gi, "\n");
      text = text.replace(
        /<a\s+[^>]*href=["']([^"']*)["'][^>]*>[^<]*<\/a>/gi,
        "$1"
      );
      text = text.replace(/<[^>]+>/g, "");
      text = text.replace(/&amp;/g, "&");
      text = text.replace(/&lt;/g, "<");
      text = text.replace(/&gt;/g, ">");
      text = text.replace(/&quot;/g, '"');
      text = text.replace(/&#39;/g, "'");
      text = text.replace(/&nbsp;/g, " ");
      text = text.replace(/\n{3,}/g, "\n\n");
      return text.trim();
    },
    matchesFieldName(key, expectedNames) {
      if (expectedNames.includes(key)) return true;
      for (const expected of expectedNames) {
        if (this.isSimilar(key, expected)) return true;
      }
      return false;
    },
    isSimilar(str1, str2) {
      if (str1.length < 3 || str2.length < 3) return false;
      if (Math.abs(str1.length - str2.length) > 1) return false;
      let differences = 0;
      const maxLen = Math.max(str1.length, str2.length);
      for (let i = 0; i < maxLen; i++) {
        if (str1[i] !== str2[i]) {
          differences++;
          if (differences > 1) return false;
        }
      }
      return true;
    },
    normalizePillarName(input) {
      const normalized = input.toLowerCase().trim();
      const pillarMap = {
        family: "Family",
        families: "Family",
        emotional: "Emotional",
        emotion: "Emotional",
        mental: "Emotional",
        physical: "Physical",
        fitness: "Physical",
        spiritual: "Spiritual",
        spirit: "Spiritual",
        faith: "Spiritual",
        social: "Social",
        community: "Social"
      };
      if (pillarMap[normalized]) return pillarMap[normalized];
      const canonicalPillars = [
        "Family",
        "Emotional",
        "Physical",
        "Spiritual",
        "Social"
      ];
      for (const pillar of canonicalPillars) {
        if (this.isSimilar(normalized, pillar.toLowerCase())) return pillar;
      }
      return null;
    }
  }
};
</script>

<style scoped lang="less">
@import "../assets/global.less";

@close-button-radius: 32px;

#close-button {
  position: absolute;
  top: -@close-button-radius;
  right: -@close-button-radius;
  height: 2 * @close-button-radius;
  width: 2 * @close-button-radius;
}

// prevent button text from being automatically capitalized
.no-text-transform {
  text-transform: none;
}

.scroll-to-top-container {
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}
</style>
